import {
  ALERT_EVENT_NAMES,
  FILTER_DETAILS,
  FILTER_TYPE,
  MANAGE_USAGE_POPUP_CLICK_LOCATION,
  POST_VOLUME_GRAPH_OPTIONS,
  SOCIAL_LISTENING_EVENT_NAMES,
  SOCIAL_LISTENING_REPORT_INTERACTION_TYPES,
  SOCIAL_LISTENING_REPORT_SECTIONS,
  SOCIAL_LISTENING_ROUTE_NAMES,
  SOCIAL_LISTENING_TOPIC_PREVIEW_SEARCH_TYPE,
} from '@/app/socialListening/constants';
import {
  formattedMediaSentiment,
  getBaseAlertProperties,
  getBaseProperties,
} from '@/app/socialListening/utils/mixpanel-events-utils';
import { stripIdFieldFromGroups } from '@/app/socialListening/utils/parsing';
import { useTrackingStore } from '@/stores/tracking';

/**
 * Mixpanel event fields already added by default:
 * brand_id, brandName, customerStage, impersonating, planType, page_sp_test
 *
 */

export function socialListeningMixpanelEvent(event, properties) {
  useTrackingStore().track(event, properties);
}

export function mediaPopupOpenedEvent({
  mediaOpened,
  mediaType,
  pageOpenedFrom,
  path,
  sourceType,
  sentiment,
}) {
  const topicEvent = [
    SOCIAL_LISTENING_ROUTE_NAMES.TOPICS,
    SOCIAL_LISTENING_ROUTE_NAMES.TOPICS_TOP_POSTS,
  ].includes(pageOpenedFrom);
  const { keywordApplied, topicName, topicID } = getBaseProperties(topicEvent);

  useTrackingStore().track(SOCIAL_LISTENING_EVENT_NAMES.MEDIA_POPUP_OPENED, {
    mediaOpened,
    mediaType,
    pageOpenedFrom,
    path,
    platform: sourceType,
    sourceType,
    keywordApplied,
    sentiment: formattedMediaSentiment(sentiment),
    topicName,
    topicID,
  });
}

export function socialListeningFiltersEvent({
  filterUsed,
  filterValue,
  section = SOCIAL_LISTENING_REPORT_SECTIONS.TOPIC,
  eventFrom,
}) {
  const topicEvent = [
    SOCIAL_LISTENING_ROUTE_NAMES.TOPICS,
    SOCIAL_LISTENING_ROUTE_NAMES.TOPICS_TOP_POSTS,
  ].includes(eventFrom);
  const quicksearchEvent = [SOCIAL_LISTENING_ROUTE_NAMES.QUICK_SEARCH].includes(eventFrom);
  const { platform, dateScale, topicName, topicID } = getBaseProperties(
    topicEvent || quicksearchEvent,
  );
  const selectedFilter = FILTER_DETAILS[filterUsed];
  const details = {
    filterUsed: selectedFilter.filter,
    action: selectedFilter?.action,
  };
  switch (filterUsed) {
    case FILTER_TYPE.VISUAL.value:
      details.visualFilterSelection = filterValue?.selection;
      details.filterValue = filterValue?.value;
      break;
    default:
      details.filterValue = topicEvent ? filterValue : (filterValue ?? selectedFilter?.default);
      break;
  }

  let payload = {
    section,
    platform,
    dateScale,
    ...details,
  };

  if (topicEvent) {
    payload = { ...payload, topicID, topicName };
  }

  useTrackingStore().track(SOCIAL_LISTENING_EVENT_NAMES.SOCIAL_LISTENING_FILTERS, payload);
}

export function socialListeningReportInteractionEvent({
  reportType,
  reportValue,
  reportDetails,
  interactionType,
  path,
}) {
  const details = {};

  switch (reportType) {
    case SOCIAL_LISTENING_REPORT_SECTIONS.SHARE_OF_VOICE_INDUSTRY:
      details.industries = reportValue;
      break;
    case SOCIAL_LISTENING_REPORT_SECTIONS.SHARE_OF_VOICE_COMPETITOR:
      details.competitors = reportValue;
      break;
    case SOCIAL_LISTENING_REPORT_SECTIONS.TOP_KEYWORDS:
      details.keyword = reportValue;
      break;
    case SOCIAL_LISTENING_REPORT_SECTIONS.TOTAL_POSTS:
      details.graphMetric = reportValue;
      if (interactionType === SOCIAL_LISTENING_REPORT_INTERACTION_TYPES.DATA_POINT_CLICKED) {
        details.platform = reportDetails.channels;
        details.sentiment = reportDetails.sentiments;
        details.mediaType = reportDetails.mediaTypes;
      } else if (reportValue === POST_VOLUME_GRAPH_OPTIONS.POSTS_BY_CHANNEL.label) {
        details.platform = reportDetails;
      } else if (reportValue === POST_VOLUME_GRAPH_OPTIONS.POSTS_BY_SENTIMENT.label) {
        details.sentiment = reportDetails;
      } else if (reportValue === POST_VOLUME_GRAPH_OPTIONS.POSTS_BY_MEDIA_TYPE.label) {
        details.mediaType = reportDetails;
      }
      break;
    default:
      break;
  }

  let payload = {
    interactionType,
    path,
    reportType,
    ...details,
  };

  if (path !== SOCIAL_LISTENING_ROUTE_NAMES.TRENDS) {
    const { topicName, topicID } = getBaseProperties(true);
    payload = { ...payload, topicName, topicID };
  }

  useTrackingStore().track(
    SOCIAL_LISTENING_EVENT_NAMES.SOCIAL_LISTENING_REPORT_INTERACTION,
    payload,
  );
}

export function visualTrendsPostsClickedEvent({ clickedFrom, path }) {
  const topicEvent = clickedFrom === SOCIAL_LISTENING_ROUTE_NAMES.TOPICS;
  const { sentiment, keywordApplied, topicName, topicID, mediaType } =
    getBaseProperties(topicEvent);

  useTrackingStore().track(SOCIAL_LISTENING_EVENT_NAMES.VISUAL_TRENDS_POST_CLICKED, {
    path,
    sentiment,
    keywordApplied,
    mediaType,
    topicName,
    topicID,
  });
}

export function topicCreationStartedEvent({
  topicInstanceID = undefined,
  pageAccess = true,
  fromTeaser = false,
  creationFlow = undefined,
}) {
  useTrackingStore().track(SOCIAL_LISTENING_EVENT_NAMES.TOPIC_CREATION_STARTED, {
    topicInstanceID,
    pageAccess,
    fromTeaser,
    creationFlow,
  });
}

export function topicCompetitorsAddedEvent({
  numberOfCompetitors,
  competitorNames,
  topicInstanceID = undefined,
}) {
  useTrackingStore().track(SOCIAL_LISTENING_EVENT_NAMES.TOPIC_COMPETITORS_ADDED, {
    numberOfCompetitors,
    competitorNames,
    topicInstanceID,
  });
}

export function topicKeywordsAddedEvent({
  inclusions,
  exclusions,
  numberOfKeywordsIncluded,
  numberOfKeywordsExcluded,
  topicInstanceID = undefined,
  keywordsString,
  listInclude,
  listExclude,
  keywordGroups,
  textEditorUsed,
  creationFlow,
}) {
  useTrackingStore().track(SOCIAL_LISTENING_EVENT_NAMES.TOPIC_KEYWORDS_ADDED, {
    inclusions,
    exclusions,
    numberOfKeywordsIncluded,
    numberOfKeywordsExcluded,
    topicInstanceID,
    keywordsString,
    listInclude,
    listExclude,
    keywordGroups: stripIdFieldFromGroups(keywordGroups),
    textEditorUsed,
    creationFlow,
  });
}

export function topicCreatedEvent({
  topicInstanceID = undefined,
  inclusions,
  exclusions,
  numberOfKeywordsIncluded,
  numberOfKeywordsExcluded,
  topicName,
  topicID,
  webSource,
  keywordsString,
  listInclude,
  listExclude,
  keywordGroups,
  fromTeaser,
  textEditorUsed,
  creationFlow,
}) {
  useTrackingStore().track(SOCIAL_LISTENING_EVENT_NAMES.TOPIC_CREATED, {
    topicInstanceID,
    inclusions,
    exclusions,
    numberOfKeywordsIncluded,
    numberOfKeywordsExcluded,
    topicName,
    topicID,
    webSource,
    keywordsString,
    listInclude,
    listExclude,
    keywordGroups: stripIdFieldFromGroups(keywordGroups),
    fromTeaser,
    textEditorUsed,
    creationFlow,
  });
}

export function topicCreationFailedEvent({ topicInstanceID = undefined, errorType }) {
  useTrackingStore().track(SOCIAL_LISTENING_EVENT_NAMES.TOPIC_CREATION_FAILED, {
    topicInstanceID,
    errorType,
  });
}

export function topicDeletedEvent({ topicID, topicName }) {
  useTrackingStore().track(SOCIAL_LISTENING_EVENT_NAMES.TOPIC_DELETED, { topicID, topicName });
}

export function topicSettingsChangedEvent({
  topicID,
  topicName,
  editContext,
  pageAccess = true,
  fromTeaser = false,
  textEditorUsed,
}) {
  useTrackingStore().track(SOCIAL_LISTENING_EVENT_NAMES.TOPIC_SETTINGS_CHANGED, {
    topicID,
    topicName,
    editContext,
    pageAccess,
    fromTeaser,
    textEditorUsed,
  });
}

export function manageUsageCtaEvent(buttonName) {
  useTrackingStore().track(SOCIAL_LISTENING_EVENT_NAMES.MANAGE_USAGE_UPGRADE_LIMITS, {
    buttonName,
  });
}

export function manageUseClickedEvent(
  topicID,
  topicName,
  clickLocation = MANAGE_USAGE_POPUP_CLICK_LOCATION.NAV_BAR,
) {
  let payload = {
    clickLocation,
  };
  if (topicName && topicID) {
    payload = { ...payload, topicID, topicName };
  }
  useTrackingStore().track(SOCIAL_LISTENING_EVENT_NAMES.MANAGE_USE_CLICKED, payload);
}

export function topicKeywordsEditedEvent({
  topicName,
  topicID,
  change,
  totalKeywords,
  keywordsString,
  listInclude,
  listExclude,
  keywordGroups,
  textEditorUsed,
}) {
  useTrackingStore().track(SOCIAL_LISTENING_EVENT_NAMES.TOPIC_KEYWORDS_EDITED, {
    topicName,
    topicID,
    change,
    totalKeywords,
    keywordsString,
    listInclude,
    listExclude,
    keywordGroups: stripIdFieldFromGroups(keywordGroups),
    textEditorUsed,
  });
}

export function topicViewed({
  topicID,
  topicName,
  visualFilter,
  inclusions,
  exclusions,
  numberOfKeywordsIncluded,
  numberOfKeywordsExcluded,
  webSource,
  pageOpenedFrom,
  keywordsString,
  listInclude,
  listExclude,
  keywordGroups,
  inComparison = false,
}) {
  useTrackingStore().track(SOCIAL_LISTENING_EVENT_NAMES.TOPIC_VIEWED, {
    topicID,
    topicName,
    visualFilter,
    inclusions,
    exclusions,
    numberOfKeywordsIncluded,
    numberOfKeywordsExcluded,
    webSource,
    pageOpenedFrom,
    keywordsString,
    listInclude,
    listExclude,
    keywordGroups: stripIdFieldFromGroups(keywordGroups),
    inComparison,
  });
}

export function seeMorePostsClickedEvent({ topicID, section }) {
  useTrackingStore().track(SOCIAL_LISTENING_EVENT_NAMES.SEE_MORE_POSTS_CLICKED, {
    topicID,
    section,
  });
}

export function seeMorePostsLoadedEvent({
  topicID,
  section,
  pageNumber,
  postsFetchedCount,
  totalPostsLoaded,
}) {
  useTrackingStore().track(SOCIAL_LISTENING_EVENT_NAMES.SEE_MORE_POSTS_LOADED, {
    topicID,
    section,
    pageNumber,
    postsFetchedCount,
    totalPostsLoaded,
  });
}

export function comparisonViewed({ comparisonId, comparisonName, topicIds }) {
  useTrackingStore().track(SOCIAL_LISTENING_EVENT_NAMES.COMPARISON_VIEWED, {
    comparisonId,
    comparisonName,
    topicIds,
  });
}

export function comparisonDeletedEvent({ comparisonId, comparisonName }) {
  useTrackingStore().track(SOCIAL_LISTENING_EVENT_NAMES.COMPARISON_DELETED, {
    comparisonId,
    comparisonName,
  });
}

export function comparisonCreationStartedEvent(creationFlow) {
  useTrackingStore().track(SOCIAL_LISTENING_EVENT_NAMES.COMPARISON_CREATION_STARTED, {
    creationFlow,
  });
}

export function comparisonCreatedEvent({
  comparisonId,
  comparisonName,
  creationFlow,
  numberOfTopicsIncluded,
  numberOfNewTopicsIncluded,
  numberOfExistingTopicsIncluded,
  topicIds,
}) {
  useTrackingStore().track(SOCIAL_LISTENING_EVENT_NAMES.COMPARISON_CREATED, {
    comparisonId,
    comparisonName,
    creationFlow,
    numberOfTopicsIncluded,
    numberOfNewTopicsIncluded,
    numberOfExistingTopicsIncluded,
    topicIds,
  });
}

export function comparisonTopicsAddedEvent({
  creationFlow,
  numberOfTopicsIncluded,
  numberOfNewTopicsIncluded,
  numberOfExistingTopicsIncluded,
}) {
  useTrackingStore().track(SOCIAL_LISTENING_EVENT_NAMES.COMPARISON_TOPICS_ADDED, {
    creationFlow,
    numberOfTopicsIncluded,
    numberOfNewTopicsIncluded,
    numberOfExistingTopicsIncluded,
  });
}

export function socialListeningCardClickedEvent({
  cardType,
  pageOpenedFrom,
  path,
  cardDetails = {},
}) {
  const topicEvent = [
    SOCIAL_LISTENING_ROUTE_NAMES.TOPICS,
    SOCIAL_LISTENING_ROUTE_NAMES.TOPICS_TOP_POSTS,
    SOCIAL_LISTENING_ROUTE_NAMES.WEB_RESULTS,
  ].includes(pageOpenedFrom);
  const { topicName, topicID } = getBaseProperties(topicEvent);
  const topicDetails = topicEvent ? { topicName, topicID } : {};
  if (cardDetails?.sentiment) {
    cardDetails.sentiment = formattedMediaSentiment(cardDetails.sentiment);
  }

  useTrackingStore().track(SOCIAL_LISTENING_EVENT_NAMES.SOCIAL_LISTENING_CARD_CLICKED, {
    cardType,
    path,
    pageOpenedFrom,
    ...topicDetails,
    ...cardDetails,
  });
}

export function alertSettingsChanged({ editContext }, useSharedStore = true) {
  useTrackingStore().track(ALERT_EVENT_NAMES.SOCIAL_LISTENING_ALERT_SETTINGS_CHANGED, {
    editContext,
    ...getBaseAlertProperties(useSharedStore),
  });
}

export function alertActivated(
  { numberOfRecipients, recipientEmails, recipientIDs, sensitivity },
  useSharedStore = true,
) {
  useTrackingStore().track(ALERT_EVENT_NAMES.SOCIAL_LISTENING_TOPIC_ALERT_ACTIVATED, {
    numberOfRecipients,
    recipientEmails,
    recipientIDs,
    sensitivity,
    ...getBaseAlertProperties(useSharedStore),
  });
}

export function alertCreated(
  { numberOfRecipients, recipientEmails, recipientIDs, sensitivity },
  useSharedStore = true,
) {
  useTrackingStore().track(ALERT_EVENT_NAMES.SOCIAL_LISTENING_TOPIC_ALERT_CREATED, {
    numberOfRecipients,
    recipientEmails,
    recipientIDs,
    sensitivity,
    ...getBaseAlertProperties(useSharedStore),
  });
}

export function alertDeactivated(
  { numberOfRecipients, recipientEmails, recipientIDs, sensitivity },
  useSharedStore = true,
) {
  useTrackingStore().track(ALERT_EVENT_NAMES.SOCIAL_LISTENING_TOPIC_ALERT_DEACTIVATED, {
    numberOfRecipients,
    recipientEmails,
    recipientIDs,
    sensitivity,
    ...getBaseAlertProperties(useSharedStore),
  });
}

export function socialListeningViewedReportInApp({ topicID, topicName, pageDirectedTo }) {
  useTrackingStore().track(SOCIAL_LISTENING_EVENT_NAMES.SOCIAL_LISTENING_VIEWED_REPORT_IN_APP, {
    topicID,
    topicName,
    pageDirectedTo,
  });
}

export function topicTextEditorSelectedEvent({ action }) {
  useTrackingStore().track(SOCIAL_LISTENING_EVENT_NAMES.TOPIC_TEXT_EDITOR_SELECTED, { action });
}

export function hoverSuggestedTopicEvent({ topicID, topicName }) {
  useTrackingStore().track(SOCIAL_LISTENING_EVENT_NAMES.SUGGESTED_TOPIC_HOVERED, {
    topicID,
    topicName,
  });
}

export function topicQuickSearchPreviewEvent({
  creationFlow,
  fromTeaser,
  searchType = SOCIAL_LISTENING_TOPIC_PREVIEW_SEARCH_TYPE.quickSearch,
}) {
  useTrackingStore().track(SOCIAL_LISTENING_EVENT_NAMES.TOPIC_QUICK_SEARCH_PREVIEW, {
    creationFlow,
    fromTeaser,
    searchType,
  });
}

export function trendingKeywordSelectedEvent({ keyword, platform, sentiment }) {
  useTrackingStore().track(SOCIAL_LISTENING_EVENT_NAMES.TRENDING_KEYWORD_SELECTED, {
    keyword,
    platform,
    sentiment,
  });
}

export function commentReplySentEvent({ topicID, topicName, source }) {
  useTrackingStore().track(SOCIAL_LISTENING_EVENT_NAMES.COMMENT_REPLY_SENT, {
    topicID,
    topicName,
    source,
  });
}
